import { useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Text,
  Alert,
  Accordion,
  ThemeIcon,
  Center,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useContentPlansStore } from "./contentPlansStore";
import { notifications } from "@mantine/notifications";
import {
  IconCalendar,
  IconUpload,
  IconX,
  IconPlugConnected,
  IconSparkles,
} from "@tabler/icons-react";

const formatDate = (date) => date.toISOString().split("T")[0];

const getDefaultStartDate = () => formatDate(new Date());

const getDefaultEndDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return formatDate(date);
};

export default function CreateContentPlanModal({ opened, onClose, brandId, gscLinked }) {
  const { createContentPlan, isLoading } = useContentPlansStore();
  const [name, setName] = useState("");
  const [themeFocus, setThemeFocus] = useState("");
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);
  const [csvFile, setCsvFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!brandId) {
      notifications.show({
        title: "Error",
        message: "Please select a brand first",
        color: "red",
      });
      return;
    }

    if (!startDate || !endDate) {
      notifications.show({
        title: "Error",
        message: "Please select start and end dates",
        color: "red",
      });
      return;
    }

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    if (startDateObj >= endDateObj) {
      notifications.show({
        title: "Error",
        message: "End date must be after start date",
        color: "red",
      });
      return;
    }

    const planData = {
      name: name.trim() || null,
      themeFocus: themeFocus.trim() || null,
      startDate: startDate,
      endDate: endDate,
      brandId: brandId,
      googleSearchConsoleCsv: csvFile,
    };

    const result = await createContentPlan(planData);
    if (result) {
      setName("");
      setThemeFocus("");
      setStartDate(getDefaultStartDate());
      setEndDate(getDefaultEndDate());
      setCsvFile(null);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Propose clusters"
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {/* Hero section */}
          <Center>
            <ThemeIcon size={48} radius="xl" variant="light" color="blue">
              <IconSparkles size={24} />
            </ThemeIcon>
          </Center>
          <Text ta="center" size="sm" c="dimmed">
            AI will read your brand, keywords, and search data and propose
            topical clusters to research and write.
          </Text>

          {/* Theme/Focus — lightweight single-line input */}
          <TextInput
            label="Theme or Focus (optional)"
            placeholder="e.g., Product launch, Sustainability initiative, Seasonal campaign"
            value={themeFocus}
            onChange={(e) => setThemeFocus(e.target.value)}
          />

          {/* Date range — pre-filled, secondary visual weight */}
          <Group grow>
            <TextInput
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              leftSection={<IconCalendar size={14} />}
              size="sm"
              required
            />
            <TextInput
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              leftSection={<IconCalendar size={14} />}
              size="sm"
              required
            />
          </Group>

          {/* GSC connected alert */}
          {gscLinked && (
            <Alert icon={<IconPlugConnected size={16} />} color="teal" variant="light" py="xs">
              <Text size="xs">
                Google Search Console is connected — keyword data will be used automatically.
              </Text>
            </Alert>
          )}

          {/* Advanced options — collapsed by default */}
          <Accordion variant="default">
            <Accordion.Item value="advanced">
              <Accordion.Control>
                <Text size="sm" c="dimmed">Advanced Options</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <TextInput
                    label="Plan Name"
                    placeholder="Leave blank to auto-generate"
                    description="Optional — auto-generated from brand name and date"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="sm"
                  />

                  {!gscLinked && (
                    <div>
                      <Text size="sm" fw={500} mb="xs">
                        Google Search Console CSV
                      </Text>
                      <Text size="xs" c="dimmed" mb="sm">
                        Upload "Queries.csv" from GSC export. Or connect GSC in Settings for
                        automatic data.
                      </Text>
                      <Dropzone
                        onDrop={(files) => setCsvFile(files[0])}
                        onReject={() =>
                          notifications.show({
                            title: "Error",
                            message: "Please upload a valid CSV file",
                            color: "red",
                          })
                        }
                        maxSize={5 * 1024 ** 2}
                        accept={["text/csv", "application/vnd.ms-excel"]}
                        multiple={false}
                      >
                        <Group
                          justify="center"
                          gap="xl"
                          mih={60}
                          style={{ pointerEvents: "none" }}
                        >
                          <Dropzone.Accept>
                            <IconUpload size={24} stroke={1.5} />
                          </Dropzone.Accept>
                          <Dropzone.Reject>
                            <IconX size={24} stroke={1.5} />
                          </Dropzone.Reject>
                          <Text size="sm" inline>
                            {csvFile ? csvFile.name : "Drag CSV here or click to select"}
                          </Text>
                        </Group>
                      </Dropzone>
                      {csvFile && (
                        <Text size="xs" c="green" mt="xs">
                          Selected: {csvFile.name}
                        </Text>
                      )}
                    </div>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              leftSection={<IconSparkles size={16} />}
            >
              Propose clusters
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
